
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ResourceClassification, ResourceEstimation, ValuationMethod } from "@/lib/types";
import { formatCurrency, formatNumber } from "@/lib/formatters";
import { KeyValuationMetrics } from "./KeyValuationMetrics";
import DataLinkButton from "@/components/ui/data-link-button";
import { DataSource } from "@/components/data-tables/DataTables";

export function JORCValuation() {
  const [classification, setClassification] = useState<ResourceClassification>("measured");
  const [tonnage, setTonnage] = useState<number>(1000000);
  const [grade, setGrade] = useState<number>(2.5);
  const [gradeUnit, setGradeUnit] = useState<string>("g/t");
  const [cutoffGrade, setCutoffGrade] = useState<number>(0.5);
  const [confidenceLevel, setConfidenceLevel] = useState<number>(80);
  const [mineralType, setMineralType] = useState<string>("Gold");
  const [valuationMethod, setValuationMethod] = useState<string>("comparable");
  const [competentPerson, setCompetentPerson] = useState<string>("");
  const [resourceValue, setResourceValue] = useState<number>(0);
  const [showValuationMetrics, setShowValuationMetrics] = useState(false);
  const [resource, setResource] = useState<ResourceEstimation | null>(null);

  // Function to calculate resource value based on inputs
  const calculateResourceValue = () => {
    let baseValue: number;
    let confidenceMultiplier: number = confidenceLevel / 100;
    let classificationMultiplier: number;

    // Set classification multiplier based on confidence in resource
    switch (classification) {
      case "exploration_target":
        classificationMultiplier = 0.1; // Lowest confidence
        break;
      case "inferred":
        classificationMultiplier = 0.3;
        break;
      case "indicated":
        classificationMultiplier = 0.6;
        break;
      case "measured":
        classificationMultiplier = 1.0; // Highest confidence
        break;
      default:
        classificationMultiplier = 0.5;
    }

    // Basic calculation based on valuation method
    if (mineralType === "Gold" || mineralType === "Silver") {
      // For precious metals
      const metalPrice = mineralType === "Gold" ? 1800 : 24; // USD per oz
      const recoveryRate = 0.9; // 90% recovery
      const processingCost = 15; // USD per tonne
      const miningCost = 20; // USD per tonne
      
      // Convert g/t to oz/t (1 g/t ≈ 0.029 oz/t)
      const ozPerTonne = grade * 0.029167;
      
      // Revenue per tonne
      const revenuePerTonne = ozPerTonne * metalPrice * recoveryRate;
      
      // Cost per tonne
      const costPerTonne = processingCost + miningCost;
      
      // Profit per tonne
      const profitPerTonne = revenuePerTonne - costPerTonne;
      
      // Total gross value
      baseValue = profitPerTonne * tonnage;
    } else {
      // For base metals and others - simplified calculation
      baseValue = tonnage * grade * 10; // Simple approximation
    }

    // Apply multipliers
    const adjustedValue = baseValue * classificationMultiplier * confidenceMultiplier;
    
    // Apply valuation method modifier
    let methodMultiplier = 1.0;
    switch (valuationMethod) {
      case "comparable":
        methodMultiplier = 0.8;
        break;
      case "income":
        methodMultiplier = 1.2;
        break;
      case "market":
        methodMultiplier = 1.0;
        break;
      case "cost":
        methodMultiplier = 0.7;
        break;
      case "real-options":
        methodMultiplier = 1.3;
        break;
      default:
        methodMultiplier = 1.0;
    }
    
    const finalValue = adjustedValue * methodMultiplier;
    setResourceValue(finalValue);
    
    // Create resource estimation object
    const newResource: ResourceEstimation = {
      classification,
      tonnage,
      grade,
      gradeUnit,
      cutoffGrade,
      mineralType,
      confidenceLevel,
      valuationMethod: {
        type: valuationMethod as "comparable" | "income" | "market" | "cost" | "real-options",
        assumptions: {
          "recovery_rate": "90%",
          "discount_rate": "10%"
        }
      },
      marketValue: finalValue,
      reportDate: new Date().toISOString().split('T')[0],
      competentPerson
    };
    
    setResource(newResource);
    setShowValuationMetrics(true);
  };

  const handleViewDataTables = () => {
    // This would normally navigate to the data tables page with the resource data
    console.log("Navigating to data tables with resource data:", resource);
    
    // In a real app, you'd use react-router to navigate or dispatch an action
    // For this example, we'll just log the action
  };

  // Create a data source object for the data tables
  const dataSource: DataSource = {
    type: "resource",
    id: "resource-1",
    name: "JORC Resource Valuation",
    sourceType: "jorc-valuation",
    data: resource
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>JORC Resource Valuation</CardTitle>
            <CardDescription>
              Calculate the value of your mineral resources using JORC-compliant estimation
            </CardDescription>
          </div>
          {resource && (
            <DataLinkButton 
              dataSource={dataSource}
              onViewData={(data) => console.log("View data tables", data)}
              label="View Resource Tables" 
            />
          )}
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="resource-parameters">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="resource-parameters">Resource Parameters</TabsTrigger>
              <TabsTrigger value="valuation-parameters">Valuation Parameters</TabsTrigger>
            </TabsList>
            
            <TabsContent value="resource-parameters" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="classification">JORC Classification</Label>
                  <Select
                    value={classification}
                    onValueChange={(value) => setClassification(value as ResourceClassification)}
                  >
                    <SelectTrigger id="classification">
                      <SelectValue placeholder="Select classification" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exploration_target">Exploration Target</SelectItem>
                      <SelectItem value="inferred">Inferred</SelectItem>
                      <SelectItem value="indicated">Indicated</SelectItem>
                      <SelectItem value="measured">Measured</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="mineralType">Mineral Type</Label>
                  <Select
                    value={mineralType}
                    onValueChange={setMineralType}
                  >
                    <SelectTrigger id="mineralType">
                      <SelectValue placeholder="Select mineral" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Gold">Gold</SelectItem>
                      <SelectItem value="Silver">Silver</SelectItem>
                      <SelectItem value="Copper">Copper</SelectItem>
                      <SelectItem value="Iron">Iron</SelectItem>
                      <SelectItem value="Rare Earth">Rare Earth</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tonnage">Resource Tonnage</Label>
                  <Input
                    id="tonnage"
                    type="number"
                    value={tonnage}
                    onChange={(e) => setTonnage(parseFloat(e.target.value))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="grade">Grade</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="grade"
                      type="number"
                      value={grade}
                      onChange={(e) => setGrade(parseFloat(e.target.value))}
                      className="flex-1"
                    />
                    <Select
                      value={gradeUnit}
                      onValueChange={setGradeUnit}
                    >
                      <SelectTrigger id="gradeUnit" className="w-24">
                        <SelectValue placeholder="Unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="g/t">g/t</SelectItem>
                        <SelectItem value="%">%</SelectItem>
                        <SelectItem value="ppm">ppm</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="cutoffGrade">Cut-off Grade</Label>
                  <Input
                    id="cutoffGrade"
                    type="number"
                    value={cutoffGrade}
                    onChange={(e) => setCutoffGrade(parseFloat(e.target.value))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confidenceLevel">Confidence Level (%)</Label>
                  <Input
                    id="confidenceLevel"
                    type="number"
                    min="0"
                    max="100"
                    value={confidenceLevel}
                    onChange={(e) => setConfidenceLevel(parseFloat(e.target.value))}
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="valuation-parameters" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="valuationMethod">Valuation Method</Label>
                  <Select
                    value={valuationMethod}
                    onValueChange={setValuationMethod}
                  >
                    <SelectTrigger id="valuationMethod">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="comparable">Comparable Transactions</SelectItem>
                      <SelectItem value="income">Income Approach</SelectItem>
                      <SelectItem value="market">Market Approach</SelectItem>
                      <SelectItem value="cost">Cost Approach</SelectItem>
                      <SelectItem value="real-options">Real Options</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="competentPerson">Competent Person</Label>
                  <Input
                    id="competentPerson"
                    type="text"
                    value={competentPerson}
                    onChange={(e) => setCompetentPerson(e.target.value)}
                    placeholder="Name of JORC competent person"
                  />
                </div>
              </div>
              
              <div className="pt-4">
                <Button onClick={calculateResourceValue}>Calculate Resource Value</Button>
              </div>
              
              {resourceValue > 0 && (
                <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-md">
                  <h3 className="font-medium mb-2">Resource Valuation Results</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Estimated Market Value:</p>
                      <p className="text-2xl font-bold">{formatCurrency(resourceValue)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Value per Tonne:</p>
                      <p className="text-2xl font-bold">{formatCurrency(resourceValue / tonnage)}</p>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      {showValuationMetrics && (
        <KeyValuationMetrics />
      )}
    </div>
  );
}
